/**
 * Thin `fetch` wrapper for the `/api/**` Cloudflare Pages Functions backend.
 *
 * Mirrors `web/functions/_lib/http.ts`'s error shape: a non-2xx response is parsed as
 * `{ error: { code, message, details } }` and re-thrown as a typed `ApiError` here, so
 * callers can branch on `err.code` the same way the server does. A 401 additionally
 * dispatches a `window` event so `auth.tsx` can clear local auth state without every
 * call site having to know that convention.
 */

/** Mirrors `ErrorCode` in `web/functions/_lib/http.ts`. Kept as a literal union, not an
 * import, because the client and the Functions runtime are separate TypeScript projects
 * with no shared build step between them. */
export type ErrorCode =
  | 'BAD_REQUEST'
  | 'VALIDATION_ERROR'
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'TRAINING_REQUIRED'
  | 'CONSENT_REQUIRED'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'RATE_LIMITED'
  | 'INTERNAL'

export class ApiError extends Error {
  readonly code: ErrorCode
  readonly status: number
  readonly details?: unknown

  constructor(code: ErrorCode, message: string, status: number, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.details = details
  }
}

/** The event `auth.tsx` listens for to clear local session state on a 401. */
export const AUTH_UNAUTHENTICATED_EVENT = 'auth:unauthenticated'

function isPlainErrorBody(payload: unknown): payload is { error: { code: string; message: string; details?: unknown } } {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'error' in payload &&
    typeof (payload as { error: unknown }).error === 'object' &&
    (payload as { error: { code?: unknown } }).error !== null
  )
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  let response: Response
  try {
    response = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: { ...init.headers },
    })
  } catch (cause) {
    throw new ApiError('INTERNAL', 'Could not reach the server. Check your connection and try again.', 0, cause)
  }

  const text = await response.text()
  let payload: unknown = null
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = null
    }
  }

  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent(AUTH_UNAUTHENTICATED_EVENT))
    }

    if (isPlainErrorBody(payload)) {
      throw new ApiError(
        payload.error.code as ErrorCode,
        payload.error.message,
        response.status,
        payload.error.details
      )
    }
    throw new ApiError('INTERNAL', `Something went wrong on our end (error ${response.status}). Please try again.`, response.status)
  }

  return payload as T
}

function withQuery(path: string, query?: Record<string, string | number | boolean | undefined>): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${path}?${qs}` : path
}

export const api = {
  get: <T>(path: string, query?: Record<string, string | number | boolean | undefined>): Promise<T> =>
    request<T>(withQuery(path, query), { method: 'GET' }),

  post: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),

  put: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),

  delete: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, {
      method: 'DELETE',
      headers: { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
}
